import React, { useState, useEffect } from 'react';
import { ref, get, set, remove, push } from 'firebase/database';
import { database } from '../../firebase';

interface Exam {
  id: string;
  name: string;
  description: string;
  subjects: string[];
  markingScheme: {
    correct: number;
    incorrect: number;
    unattempted: number;
  };
  status: 'active' | 'inactive';
}

export const ExamManagement: React.FC = () => {
  const [exams, setExams] = useState<Exam[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingExam, setEditingExam] = useState<Exam | null>(null);

  const [newExam, setNewExam] = useState<Partial<Exam>>({
    name: '',
    description: '',
    subjects: [],
    markingScheme: {
      correct: 4,
      incorrect: -1,
      unattempted: 0
    },
    status: 'active'
  });

  const [newSubject, setNewSubject] = useState('');

  useEffect(() => {
    fetchExams();
  }, []);

  const fetchExams = async () => {
    try {
      const examsRef = ref(database, 'exams');
      const snapshot = await get(examsRef);
      
      if (snapshot.exists()) {
        const examsData = snapshot.val();
        const examsList = Object.entries(examsData).map(([id, data]: [string, any]) => ({
          id,
          ...data
        }));
        setExams(examsList);
      }
    } catch (error) {
      console.error('Error fetching exams:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveExam = async () => {
    try {
      if (editingExam) {
        const examRef = ref(database, `exams/${editingExam.id}`);
        await set(examRef, newExam);
        setExams(exams.map(e => 
          e.id === editingExam.id ? { ...newExam, id: editingExam.id } as Exam : e
        ));
      } else {
        const examsRef = ref(database, 'exams');
        const newExamRef = push(examsRef);
        await set(newExamRef, newExam);
        setExams([...exams, { ...newExam, id: newExamRef.key } as Exam]);
      }
      
      resetForm();
    } catch (error) {
      console.error('Error saving exam:', error);
    }
  };

  const deleteExam = async (id: string) => {
    if (!confirm('Are you sure you want to delete this exam?')) return;
    
    try {
      const examRef = ref(database, `exams/${id}`);
      await remove(examRef);
      setExams(exams.filter(e => e.id !== id));
    } catch (error) {
      console.error('Error deleting exam:', error);
    }
  };

  const resetForm = () => {
    setNewExam({
      name: '',
      description: '',
      subjects: [],
      markingScheme: {
        correct: 4,
        incorrect: -1,
        unattempted: 0
      },
      status: 'active'
    });
    setNewSubject('');
    setShowAddForm(false);
    setEditingExam(null);
  };

  const startEdit = (exam: Exam) => {
    setNewExam(exam);
    setEditingExam(exam);
    setShowAddForm(true);
  };

  const addSubject = () => {
    if (newSubject.trim() && !newExam.subjects?.includes(newSubject.trim())) {
      setNewExam({
        ...newExam,
        subjects: [...(newExam.subjects || []), newSubject.trim()]
      });
      setNewSubject('');
    }
  };

  const removeSubject = (subject: string) => {
    setNewExam({
      ...newExam,
      subjects: newExam.subjects?.filter(s => s !== subject) || []
    });
  };

  const ExamForm = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold">
              {editingExam ? 'Edit Exam' : 'Add New Exam'}
            </h3>
            <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Exam Name</label>
              <input
                type="text"
                value={newExam.name}
                onChange={(e) => setNewExam({...newExam, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., NDA, JEE Main"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={newExam.description}
                onChange={(e) => setNewExam({...newExam, description: e.target.value})}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Enter exam description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subjects</label>
              <div className="flex space-x-2 mb-2">
                <input
                  type="text"
                  value={newSubject}
                  onChange={(e) => setNewSubject(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addSubject()}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="Add subject"
                />
                <button
                  onClick={addSubject}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {newExam.subjects?.map((subject, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-full"
                  >
                    {subject}
                    <button
                      onClick={() => removeSubject(subject)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Marking Scheme</label>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Correct Answer</label>
                  <input
                    type="number"
                    value={newExam.markingScheme?.correct}
                    onChange={(e) => setNewExam({
                      ...newExam,
                      markingScheme: {
                        ...newExam.markingScheme!,
                        correct: parseInt(e.target.value)
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Incorrect Answer</label>
                  <input
                    type="number"
                    value={newExam.markingScheme?.incorrect}
                    onChange={(e) => setNewExam({
                      ...newExam,
                      markingScheme: {
                        ...newExam.markingScheme!,
                        incorrect: parseInt(e.target.value)
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Unattempted</label>
                  <input
                    type="number"
                    value={newExam.markingScheme?.unattempted}
                    onChange={(e) => setNewExam({
                      ...newExam,
                      markingScheme: {
                        ...newExam.markingScheme!,
                        unattempted: parseInt(e.target.value)
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={newExam.status}
                onChange={(e) => setNewExam({...newExam, status: e.target.value as any})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={resetForm}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={saveExam}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                {editingExam ? 'Update' : 'Add'} Exam
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          {[1, 2].map(i => (
            <div key={i} className="h-24 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold">Exam Management ({exams.length})</h3>
        <button
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Add Exam
        </button>
      </div>

      <div className="space-y-4">
        {exams.map((exam) => (
          <div key={exam.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <h4 className="text-lg font-medium text-gray-900">{exam.name}</h4>
                  <span className={`px-2 py-1 text-xs rounded ${
                    exam.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {exam.status}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-2">{exam.description}</p>
                <div className="flex flex-wrap gap-1 mb-2">
                  {exam.subjects?.map((subject, index) => (
                    <span key={index} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                      {subject}
                    </span>
                  ))}
                </div>
                <div className="text-xs text-gray-500">
                  Marking: +{exam.markingScheme?.correct} / {exam.markingScheme?.incorrect} / {exam.markingScheme?.unattempted}
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => startEdit(exam)}
                  className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  Edit
                </button>
                <button
                  onClick={() => deleteExam(exam.id)}
                  className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showAddForm && <ExamForm />}
    </div>
  );
};